import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import {
  Flex,
  Span,
  Table,
  Button,
  Tooltip,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Pagination,
  TableActions,
  OrderableTableRow,
  useOrderableTableReducer
} from '@sopranodesign/pulse-components';
import { PermissionFilter } from '@sopranodesign/pulse-auth';

const ZendeskCategoriesList = ({
  categories = [],
  settings,
  onDisconnect,
  onView,
  onResync,
  onDelete,
  onAddCategory,
  onResyncAll,
  isAddCategoryLoading,
  pagination = {},
}) => {
  const { meta } = pagination;
  const order = useOrderableTableReducer({ id: 'zendesk-categories' });

  const sortedCategories = React.useMemo(() => {
    if (!order.params.order) { return categories; }
    const sorted = [...categories].sort((a, b) => {
      const dir = order.params.direction === 'descending' ? -1 : 1;
      const key = order.params.order;
      if (a[key] < b[key]) { return -1 * dir; }
      if (a[key] > b[key]) { return 1 * dir; }
      return 0;
    });
    return sorted;
  }, [categories, order.params]);

  return (
    <Flex col>
      <Flex border xScroll>
        <Table>
          <TableHead>
            <OrderableTableRow
              rows={[
                { label: 'Category', sort: 'name' },
                { label: 'Last Sync', sort: 'updated_at' },
                null
              ]}
              {...order.props}
            />
          </TableHead>
          <TableBody>
            {sortedCategories.map(category => (
              <TableRow key={category.id}>
                <TableCell>{category.name}</TableCell>
                <TableCell>{category.updated_at}</TableCell>
                <TableActions>
                  <Button
                    variant="secondary"
                    icon="eye"
                    onClick={() => onView(category.id)}
                  >
                    View
                  </Button>

                  <PermissionFilter can="edit generative ai resource">
                    <Button
                      variant="secondary"
                      icon="refresh"
                      onClick={() => onResync(category)}
                    >
                      Resync
                    </Button>

                    <Button
                      variant="secondary"
                      icon="trash"
                      colour="danger"
                      onClick={() => onDelete(category)}
                    >
                      Delete
                    </Button>
                  </PermissionFilter>
                </TableActions>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Flex>

      {typeof meta.current_page === 'number' && (
        <Pagination pagination={meta} {...pagination.props} />
      )}

      <PermissionFilter can="edit generative ai resource">
        <Flex center wrap="true" gap="2" right mt>
          <Button
            ml
            icon="plus"
            variant="secondary"
            onClick={onAddCategory}
            loading={isAddCategoryLoading}
          >
            Add Categories
          </Button>
          <Button
            ml
            icon="refresh"
            variant="secondary"
            onClick={onResyncAll}
          >
            Sync All
          </Button>
          <Button
            ml
            icon="trash"
            variant="secondary"
            colour="danger"
            onClick={onDisconnect}
          >
            Disconnect
          </Button>
        </Flex>
      </PermissionFilter>

      <Flex center right mt>
        <Span light tinyText>
          <Tooltip
            position="RIGHT"
            tooltip={<Tooltip.Content>{dayjs(settings?.updated_at).format('YYYY-MM-DD HH:mm:ss')}</Tooltip.Content>}
          >
            Last sync {dayjs(settings?.updated_at).from(dayjs())}
          </Tooltip>
        </Span>
      </Flex>
    </Flex>
  );
};

ZendeskCategoriesList.propTypes = {
  categories: PropTypes.array.isRequired,
  settings: PropTypes.object,
  loadingIds: PropTypes.object,
  isSyncing: PropTypes.bool,
  onDisconnect: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  onResync: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onAddCategory: PropTypes.func.isRequired,
  onResyncAll: PropTypes.func.isRequired,
  isAddCategoryLoading: PropTypes.bool,
  pagination: PropTypes.object,
  hasPending: PropTypes.bool.isRequired
};

export default ZendeskCategoriesList;